import Sentry from "@similarweb/sentry-logger";

const CLOSING_FENCE = "```";
const NOT_FOUND = -1;

type JsonBlockReplacer = (parsed: Record<string, unknown>) => Record<string, unknown> | null;

export type JsonBlockMatch = {
    jsonContent: string;
    parsed: Record<string, unknown>;
    blockEnd: number;
};

/**
 * Finds valid JSON by trying JSON.parse at each candidate ``` fence.
 * Handles triple-backticks embedded inside JSON string values.
 *
 * When `allowTrailing` is true, also attempts to parse trailing content
 * without a closing fence (for streaming scenarios with incomplete fencing).
 */
export const findValidJsonBlock = (
    content: string,
    jsonStart: number,
    options?: { allowTrailing?: boolean },
): JsonBlockMatch | null => {
    let searchFrom = jsonStart;

    while (searchFrom < content.length) {
        const fencePos = content.indexOf(CLOSING_FENCE, searchFrom);
        if (fencePos === NOT_FOUND) {
            break;
        }

        const candidate = content.slice(jsonStart, fencePos).trim();
        try {
            const parsed = JSON.parse(candidate) as Record<string, unknown>;
            return { jsonContent: candidate, parsed, blockEnd: fencePos + CLOSING_FENCE.length };
        } catch {
            searchFrom = fencePos + CLOSING_FENCE.length;
        }
    }

    if (options?.allowTrailing) {
        const trailing = content
            .slice(jsonStart)
            .replace(/`{1,2}\s*$/, "")
            .trim();
        if (trailing) {
            try {
                const parsed = JSON.parse(trailing) as Record<string, unknown>;
                return { jsonContent: trailing, parsed, blockEnd: content.length };
            } catch {
                /* streaming — incomplete JSON */
            }
        }
    }

    return null;
};

/**
 * Replaces ```json fenced blocks in a message string.
 * Uses robust fence matching that handles triple-backticks embedded inside JSON
 * string values (e.g. generatedEmail with SSE content).
 *
 * The replacer receives the parsed JSON object. Return a new object to replace
 * the block, or null to leave it unchanged.
 */
export const replaceWidgetJsonBlocks = (message: string, replacer: JsonBlockReplacer, indent = 2): string => {
    const openingFence = /```json\r?\n/g;
    let result = "";
    let lastIndex = 0;
    let match: RegExpExecArray | null;

    while ((match = openingFence.exec(message)) !== null) {
        const jsonStart = match.index + match[0].length;
        const extracted = findValidJsonBlock(message, jsonStart);

        if (!extracted) {
            Sentry.errorTracker({
                message: "Failed to parse JSON in fenced block",
                e: new Error("Invalid JSON block in message"),
                level: "warning",
            });
            openingFence.lastIndex = jsonStart;
            continue;
        }

        const replacement = replacer(extracted.parsed);
        if (replacement !== null) {
            result += message.slice(lastIndex, match.index);
            result += `\`\`\`json\n${JSON.stringify(replacement, null, indent)}\n\`\`\``;
            lastIndex = extracted.blockEnd;
        }

        openingFence.lastIndex = extracted.blockEnd;
    }

    result += message.slice(lastIndex);
    return result;
};
